import { PositionModel, Unit } from '../Generated/Commodity-Management-Api';

export const convertVolume = (
    volume: number,
    unitFrom: Unit,
    unitTo: Unit,
    bushelsPerPound?: number,
    contractQuantity?: number,
    contractUnit?: Unit
): number => {
    if (unitFrom === unitTo) {
        return volume;
    }

    if (unitFrom === Unit.Contract) {
        if (!contractQuantity || !contractUnit) {
            return volume;
        }
        return convertVolume(
            volume * contractQuantity,
            contractUnit,
            unitTo,
            bushelsPerPound,
            contractQuantity,
            contractUnit
        );
    }

    if (unitTo === Unit.Contract) {
        if (!contractQuantity || !contractUnit) {
            return volume;
        }

        const quantityInContractUnit = convertVolume(
            volume,
            unitFrom,
            contractUnit,
            bushelsPerPound,
            contractQuantity,
            contractUnit
        );

        return quantityInContractUnit / contractQuantity;
    }

    const toPounds = (val: number, unit: Unit): number | null => {
        switch (unit) {
            case Unit.Lb:
                return val;
            case Unit.Ton:
                return val * 2000;
            case Unit.Tonne:
                return val * 2204.62262;
            case Unit.Kg:
                return val * 2.20462262;
            case Unit.Cwt:
                return val * 100;
            case Unit.Toz: // Troy Ounce
                return val * 0.06857142857;
            case Unit.Bu:
                // bushelsPerPound is (Bushels / Pounds)
                // Lbs = Bushels / (Bushels / Pound)
                return bushelsPerPound ? val * bushelsPerPound : null;
            default:
                return null;
        }
    };

    const fromPounds = (val: number, unit: Unit): number | null => {
        switch (unit) {
            case Unit.Lb:
                return val;
            case Unit.Ton:
                return val / 2000;
            case Unit.Tonne:
                return val / 2204.62262;
            case Unit.Kg:
                return val / 2.20462262;
            case Unit.Cwt:
                return val / 100;
            case Unit.Toz:
                return val / 0.06857142857;
            case Unit.Bu:
                // Bushels = Lbs * (Bushels / Pound)
                return bushelsPerPound ? val / bushelsPerPound : null;
            default:
                return null;
        }
    };

    const pounds = toPounds(volume, unitFrom);
    if (pounds === null) {
        return volume;
    }

    const result = fromPounds(pounds, unitTo);
    if (result === null) {
        return volume;
    }

    return result;
};

export const convertPrice = (
    price: number,
    unitFrom: Unit,
    unitTo: Unit,
    bushelsPerPound?: number,
    contractQuantity?: number,
    contractUnit?: Unit
): number => {
    if (unitFrom === unitTo) {
        return price;
    }

    // A price denominated in Contract is already per unit of the contract's underlying
    // unit - a corn future priced "per contract" is quoted per bushel, not per 5,000
    // bushel lot - so switching to or from Contract only relabels the unit. Dividing by
    // the lot size here turned a $4.79/bu future into $0.0377/tonne.
    //
    // This mirrors UnitConversions.ConvertPrice in CommodityManagementApi, which is what
    // prices these positions server-side. Note the deliberate asymmetry with
    // convertVolume above, where Contract DOES multiply by the lot size: volume in
    // contracts means lots, price in contracts does not.
    if (unitFrom === Unit.Contract) {
        if (!contractUnit) {
            return price;
        }
        return convertPrice(price, contractUnit, unitTo, bushelsPerPound, contractQuantity, contractUnit);
    }

    if (unitTo === Unit.Contract) {
        if (!contractUnit) {
            return price;
        }
        return convertPrice(price, unitFrom, contractUnit, bushelsPerPound, contractQuantity, contractUnit);
    }

    // Price conversion is the inverse of volume conversion
    // If 1 ton = 2000 lbs, then $/ton = $/lb * 2000
    const volumeConversionFactor = convertVolume(1, unitFrom, unitTo, bushelsPerPound);
    
    if (volumeConversionFactor === 1 && unitFrom !== unitTo) {
        // Conversion failed, return original price
        return price;
    }

    return price / volumeConversionFactor;
};

/**
 * A copy of `position` with every per-unit figure restated in `toUnit`, and
 * `unit` set to match so the Unit column, card labels and any downstream
 * `position.unit` check agree with the numbers next to them.
 *
 * contractVolume/contractUnit are always forwarded from the position's pricing
 * strategy. Without them convertVolume/convertPrice return the input unchanged
 * for anything denominated in Contract, which is most futures - so omitting
 * them makes the conversion silently no-op on exactly the positions that need
 * it.
 *
 * markToMarketValue and scenarioMarkToMarketValue are deliberately not touched:
 * they are currency totals (volume x price), so the unit cancels out and
 * scaling them would change the money. Same reasoning for `delta`, which is
 * already expressed against the position's own volume.
 */
export const convertPositionToUnit = (position: PositionModel, toUnit: Unit | undefined | null): PositionModel => {
    if (!toUnit || !position.unit || position.unit === toUnit) {
        return position;
    }

    const { bushelVolume, contractVolume, contractUnit } = position.positionPricingStrategy ?? {};

    const asVolume = (value: number | undefined | null) =>
        value == null ? value : convertVolume(value, position.unit, toUnit, bushelVolume ?? undefined, contractVolume ?? undefined, contractUnit);

    const asPrice = (value: number | undefined | null) =>
        value == null ? value : convertPrice(value, position.unit, toUnit, bushelVolume ?? undefined, contractVolume ?? undefined, contractUnit);

    return {
        ...position,
        unit: toUnit,
        volume: asVolume(position.volume)!,
        effectiveVolume: asVolume(position.effectiveVolume)!,
        price: asPrice(position.price)!,
        marketPrice: asPrice(position.marketPrice),
        markToMarketPrice: asPrice(position.markToMarketPrice),
        scenarioMarketPrice: asPrice(position.scenarioMarketPrice),
        scenarioMarkToMarketPrice: asPrice(position.scenarioMarkToMarketPrice),
        optionStrike: asPrice(position.optionStrike),
    };
};